import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, Film, MapPin } from 'lucide-react';
import axios from 'axios';
import { SEO } from '@/components/common/SEO';
import { PageHeroBanner } from '@/components/common/PageHeroBanner';
import { Button } from '@/components/ui/button';
import { resolveMediaUrl } from '@/utils/helpers';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const REGIONS = [
  { value: 'all',     label: 'All Regions' },
  { value: 'west',    label: 'West' },
  { value: 'east',    label: 'East' },
  { value: 'central', label: 'Central' },
  { value: 'south',   label: 'South' },
];

const REGION_LABEL = { west: 'West', east: 'East', central: 'Central', south: 'South' };

const regionBadgeClasses = (region) => {
  switch (region) {
    case 'west':    return 'bg-wpsl-red/15 text-wpsl-red border-wpsl-red/40';
    case 'east':    return 'bg-wpsl-blue/15 text-wpsl-blue border-wpsl-blue/40';
    case 'central': return 'bg-amber-500/15 text-amber-400 border-amber-500/40';
    case 'south':   return 'bg-emerald-500/15 text-emerald-400 border-emerald-500/40';
    default:        return 'bg-gray-700/40 text-gray-300 border-gray-600';
  }
};

export const PartnersPage = () => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [regionFilter, setRegionFilter] = useState('all');

  useEffect(() => {
    axios.get(`${API}/production-companies/directory`)
      .then(res => setPartners(res.data || []))
      .catch(() => setPartners([]))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    if (regionFilter === 'all') return partners;
    return partners.filter(p => (p.regions || []).includes(regionFilter));
  }, [partners, regionFilter]);

  const totalStats = useMemo(() => ({
    partners: partners.length,
    games: partners.reduce((sum, p) => sum + (p.games_count || 0), 0),
  }), [partners]);

  return (
    <div data-testid="partners-page">
      <SEO
        title="Production Partners"
        description="Meet the production companies and broadcast crews bringing WPSL matches to you. Browse partners by region and coverage."
        url="/partners"
      />

      <PageHeroBanner
        pageSlug="partners"
        fallbackTitle="Production Partners"
        fallbackSubtitle="The crews and companies bringing WPSL matches to screens across the country"
      />

      <div className="min-h-screen py-10 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Top stats strip + region filter */}
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
            <div className="flex gap-6">
              <div>
                <div
                  className="text-4xl font-black text-white leading-none"
                  style={{ fontFamily: 'Oswald, sans-serif', letterSpacing: '0.02em' }}
                  data-testid="partners-count"
                >
                  {totalStats.partners}
                </div>
                <div className="text-xs text-gray-400 uppercase tracking-widest mt-1">Partners</div>
              </div>
              <div>
                <div
                  className="text-4xl font-black text-wpsl-red leading-none"
                  style={{ fontFamily: 'Oswald, sans-serif', letterSpacing: '0.02em' }}
                  data-testid="partners-total-games"
                >
                  {totalStats.games}
                </div>
                <div className="text-xs text-gray-400 uppercase tracking-widest mt-1">Matches Produced</div>
              </div>
            </div>

            {/* Region filter pills */}
            <div className="flex flex-wrap gap-2" role="tablist" aria-label="Filter partners by region">
              {REGIONS.map(r => {
                const active = regionFilter === r.value;
                return (
                  <button
                    key={r.value}
                    onClick={() => setRegionFilter(r.value)}
                    className={`px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wide transition-colors ${
                      active
                        ? 'bg-wpsl-red text-white'
                        : 'bg-gray-800/60 text-gray-300 hover:bg-gray-700 border border-gray-700'
                    }`}
                    data-testid={`partners-region-filter-${r.value}`}
                  >
                    {r.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Loading state */}
          {loading && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bg-gray-800/50 rounded-xl p-6 border border-gray-700 animate-pulse">
                  <div className="h-16 bg-gray-700 rounded w-32 mb-4" />
                  <div className="h-5 bg-gray-700 rounded w-48 mb-2" />
                  <div className="h-4 bg-gray-700 rounded w-32" />
                </div>
              ))}
            </div>
          )}

          {/* Empty state */}
          {!loading && filtered.length === 0 && (
            <div className="text-center py-20 bg-gray-800/30 rounded-xl border border-gray-700" data-testid="partners-empty">
              <Film className="w-12 h-12 text-gray-600 mx-auto mb-4" />
              <h3 className="text-white text-lg font-semibold mb-2">
                {regionFilter === 'all' ? 'No production partners yet' : `No partners covering WPSL ${REGION_LABEL[regionFilter]}`}
              </h3>
              <p className="text-gray-500 text-sm max-w-md mx-auto">
                {regionFilter === 'all'
                  ? 'Production partners appear here automatically once they\'re credited on a game.'
                  : 'Try another region, or check back as coverage expands.'}
              </p>
              {regionFilter !== 'all' && (
                <Button
                  onClick={() => setRegionFilter('all')}
                  className="mt-5 bg-wpsl-red hover:bg-wpsl-blue text-white rounded-full px-6 transition-colors"
                >
                  View all partners
                </Button>
              )}
            </div>
          )}

          {/* Grid */}
          {!loading && filtered.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map(partner => (
                <article
                  key={partner.id}
                  className="group bg-gradient-to-br from-[#001428] to-[#000814] rounded-xl p-6 border border-wpsl-blue/20 hover:border-wpsl-blue/60 transition-colors flex flex-col"
                  data-testid={`partner-card-${partner.id}`}
                >
                  {/* Logo row */}
                  <div className="flex items-start justify-between mb-5">
                    <div className="flex items-center gap-4 min-w-0">
                      {partner.logo ? (
                        <img
                          src={resolveMediaUrl(partner.logo)}
                          alt={partner.name}
                          className="h-14 w-14 object-contain bg-white/5 rounded-lg p-1.5 flex-shrink-0"
                        />
                      ) : (
                        <div className="h-14 w-14 rounded-lg bg-wpsl-blue/20 flex items-center justify-center flex-shrink-0">
                          <Film className="w-7 h-7 text-wpsl-blue" />
                        </div>
                      )}
                      <h3
                        className="text-xl font-bold text-white leading-tight truncate"
                        style={{ fontFamily: 'Montserrat, sans-serif' }}
                        title={partner.name}
                      >
                        {partner.name}
                      </h3>
                    </div>
                  </div>

                  {/* Stats */}
                  <div className="flex items-baseline gap-2 mb-4">
                    <span
                      className="text-3xl font-black text-wpsl-red"
                      style={{ fontFamily: 'Oswald, sans-serif' }}
                    >
                      {partner.games_count || 0}
                    </span>
                    <span className="text-sm text-gray-400 uppercase tracking-wider">
                      match{partner.games_count === 1 ? '' : 'es'} produced
                    </span>
                  </div>

                  {/* Coverage */}
                  <div className="mb-5 flex-1">
                    <div className="text-[11px] text-gray-500 uppercase tracking-widest mb-2 flex items-center gap-1.5">
                      <MapPin className="w-3 h-3" /> Coverage
                    </div>
                    {partner.conferences && partner.conferences.length > 0 ? (
                      <div className="flex flex-wrap gap-1.5">
                        {partner.conferences.slice(0, 6).map(conf => (
                          <Link
                            key={conf.id}
                            to={`/league/${conf.slug}`}
                            className={`text-xs px-2.5 py-1 rounded-full border transition-colors hover:brightness-125 ${regionBadgeClasses(conf.region)}`}
                            title={`${conf.games_count} match${conf.games_count === 1 ? '' : 'es'} in ${conf.name}`}
                          >
                            {conf.name}
                          </Link>
                        ))}
                        {partner.conferences.length > 6 && (
                          <span className="text-xs px-2 py-1 text-gray-500">
                            +{partner.conferences.length - 6} more
                          </span>
                        )}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500 italic">Coming soon</p>
                    )}
                  </div>

                  {/* Website link */}
                  {partner.website && (
                    <a
                      href={partner.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-sm text-wpsl-blue hover:text-white transition-colors mt-auto"
                    >
                      Visit site <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  )}
                </article>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PartnersPage;
